// CreateNewSection.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './CreateNewSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Divider from 'components/divider/Divider';
import TextInput from 'components/textinput/TextInput';
import FindExistingSection from 'components/addmodal/sections/find/FindExistingSection';
import { ADD_MODAL_SPECIAL_KEYS } from 'node/tabs/unifiedNodeTabKeys';

const MEDIA_ACTION_LABELS = Object.freeze({
  PHOTO: 'Photos',
  VIDEO: 'Videos',
  DOCUMENT: 'Documents',
  FILE: 'Files',
});

const HIDDEN_TYPE_KEYS = new Set(['NODE', 'USER', 'LIVE_ROOM', 'LIVE_STREAM']);

const INJECTED_ADD_TYPE_DEFS = Object.freeze([
  {
    key: ADD_MODAL_SPECIAL_KEYS.MISSION,
    label: 'Mission',
    iconKey: 'rocket_launch',
  },
  {
    key: ADD_MODAL_SPECIAL_KEYS.PORTAL,
    label: 'Portal',
    iconKey: 'captive_portal',
  },
]);
const VIEW_MODES = Object.freeze({
  GRID: 'GRID',
  LIST: 'LIST',
});

/**
 * CreateNewSection
 *
 * Renders the UI for creating a new node type in the Add Modal.
 *
 * @param {array} availableTypes - Available node type definitions that can be created.
 * @param {function} onTypeSelect - Handler function to call when a node type is selected for creation.
 * @param {function} onMoreClick - Handler function to open the secondary "More" panel.
 * @param {function} onClose - Handler function to call when the section should signal that it can be closed (e.g. after selecting a type).
 * @param {boolean} showSearchInput - Whether to display the search input.
 * @param {string} searchTerm - Current search text value.
 * @param {function} setSearchTerm - Setter for search term updates.
 * @param {string} searchError - Search error message.
 * @param {boolean} searching - Whether existing-search is in progress.
 * @param {boolean} noResults - Whether no results were found.
 * @param {array} searchResults - Existing-search result rows.
 * @param {object} availableTypeMap - Available type map used by existing-search rendering.
 * @param {string} anchorNodeKey - Anchor node key for attach operations.
 * @param {string} attachingNodeKey - Attaching node key for attach operations.
 * @param {function} onAttachExisting - Handler for attaching an existing node.
 *
 * @returns {ReactNode} The rendered component.
 */
const CreateNewSection = React.memo(({
  availableTypes,
  enableSelectionWithoutAnchor = false,
  onTypeSelect,
  onMoreClick,
  onClose,
  showMoreCard = true,
  showSearchInput = false,
  searchTerm = '',
  setSearchTerm,
  searchError,
  searching,
  noResults,
  searchResults,
  availableTypeMap,
  anchorNodeKey,
  attachingNodeKey,
  onAttachExisting,
  showInjectedAddTypes = false,
}) => {
  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  // Hovered type card key.
  const [hoveredTypeKey, setHoveredTypeKey] = useState('');

  // Selected display mode for workspace types.
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);

  // ----- MEMOS ----- //

  // Visible type definitions, excluding hidden system keys.
  const visibleTypes = useMemo(() => {
    return (availableTypes || []).filter((typeDef) => {
      const normalizedKey = String(typeDef?.key || '').toUpperCase();
      return !HIDDEN_TYPE_KEYS.has(normalizedKey);
    });
  }, [availableTypes]);

  // Alphabetized types by display action label.
  const sortedTypes = useMemo(() => {
    return [...visibleTypes].sort((a, b) => {
      const aLabel = (MEDIA_ACTION_LABELS[a?.key] || a?.label || '').toLowerCase();
      const bLabel = (MEDIA_ACTION_LABELS[b?.key] || b?.label || '').toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }, [visibleTypes]);

  const displayTypes = useMemo(() => {
    if (!showInjectedAddTypes) return sortedTypes;
    return [...INJECTED_ADD_TYPE_DEFS, ...sortedTypes];
  }, [showInjectedAddTypes, sortedTypes]);

  // Whether search mode should render existing-node results.
  const hasSearchQuery = String(searchTerm || '').trim().length > 0;

  // ----- HANDLERS ----- //

  /**
   * Handles selecting a type key for creation.
   *
   * @param {string} typeKey - Type key to create.
   */
  const handleTypeSelect = (typeKey) => {
    if (typeof onTypeSelect === 'function') onTypeSelect(typeKey);
    onClose?.();
  };

  /**
   * Renders the icon for a type definition or fallback action.
   *
   * @param {Object} options - Render options.
   * @param {string} options.iconKey - Material icon key.
   * @param {string} options.iconPath - Optional custom SVG path.
   * @param {number} options.size - Icon size.
   *
   * @returns {ReactNode} Icon markup.
   */
  const renderTypeIcon = ({ iconKey, iconPath, size }) => {
    if (iconKey) {
      return (
        <Icon
          iconKey={iconKey}
          width={size}
          height={size}
          color={theme.foreground}
        />
      );
    }

    return (
      <Icon
        path={iconPath}
        width={size}
        height={size}
        color={theme.foreground}
      />
    );
  };

  /**
   * Renders the card grid for a list of type definitions.
   *
   * @param {array} types - Type definitions to render.
   *
   * @returns {ReactNode} Grid markup for type cards.
   */
  const renderTypeGrid = (types, { includeMoreCard = false } = {}) => (
    <div className="create-new-section-grid">
      {types.map((typeDef) => {
        const iconKeyValue = typeDef.iconKey || (!typeDef.iconPath ? 'deployed_code' : null);
        const actionLabel = MEDIA_ACTION_LABELS[typeDef.key] || typeDef.label;

        return (
          <div
            key={typeDef.key}
            className="create-new-section-card"
            style={{
              backgroundColor: hoveredTypeKey === typeDef.key ? theme.backgroundHover : theme.panel,
              borderColor: hoveredTypeKey === typeDef.key ? theme.foregroundMuted : 'transparent',
            }}
            onClick={() => handleTypeSelect(typeDef.key)}
            onMouseEnter={() => setHoveredTypeKey(typeDef.key)}
            onMouseLeave={() => setHoveredTypeKey('')}
          >
            {renderTypeIcon({
              iconKey: iconKeyValue,
              iconPath: typeDef.iconPath,
              size: 30,
            })}
            <div className="create-new-section-card-text">
              <div
                className="create-new-section-card-label"
                style={{ color: theme.foreground }}
              >
                {actionLabel}
              </div>
              {/**
              {actionDescription ? (
                <div
                  className="create-new-section-card-description"
                  style={{ color: theme.foregroundMuted }}
                >
                  {actionDescription}
                </div>
              ) : null}
                */}
            </div>
          </div>
        );
      })}

      {includeMoreCard && (
        <div
          key="more"
          className="create-new-section-card"
          style={{
            backgroundColor: hoveredTypeKey === 'more' ? theme.backgroundHover : theme.backgroundMuted,
            borderColor: hoveredTypeKey === 'more' ? theme.foregroundMuted : 'transparent',
          }}
          onClick={onMoreClick}
          onMouseEnter={() => setHoveredTypeKey('more')}
          onMouseLeave={() => setHoveredTypeKey('')}
        >
          {renderTypeIcon({ iconKey: 'more_horiz', size: 30 })}
          <div
            className="create-new-section-card-label"
            style={{ color: theme.foreground }}
          >
            More
          </div>
        </div>
      )}
    </div>
  );

  /**
   * Renders the vertical list for workspace-scoped type definitions.
   *
   * @param {array} types - Type definitions to render.
   * @param {Object} options - List render options.
   * @param {boolean} options.includeMoreRow - Whether to append the more action row.
   *
   * @returns {ReactNode} List markup for workspace types.
   */
  const renderTypeList = (types, { includeMoreRow = false } = {}) => (
    <div className="create-new-section-list">
      {types.map((typeDef) => {
        const iconKeyValue = typeDef.iconKey || (!typeDef.iconPath ? 'deployed_code' : null);
        const actionLabel = MEDIA_ACTION_LABELS[typeDef.key] || typeDef.label;
        const actionDescription = typeof typeDef?.description === 'string'
          ? typeDef.description.trim()
          : '';

        return (
          <div
            key={typeDef.key}
            className="create-new-section-list-item"
            style={{
              backgroundColor: hoveredTypeKey === typeDef.key ? theme.backgroundHover : theme.backgroundMuted,
              borderColor: hoveredTypeKey === typeDef.key ? theme.foregroundMuted : 'transparent',
            }}
            onClick={() => handleTypeSelect(typeDef.key)}
            onMouseEnter={() => setHoveredTypeKey(typeDef.key)}
            onMouseLeave={() => setHoveredTypeKey('')}
          >

            {/* LIST ICON */}
            <div className="create-new-section-list-icon">
              {renderTypeIcon({
                iconKey: iconKeyValue,
                iconPath: typeDef.iconPath,
                size: 22,
              })}
            </div>

            {/* LIST CONTENT */}
            <div className="create-new-section-list-content">
              <div
                className="create-new-section-list-title"
                style={{ color: theme.foreground }}
              >
                {actionLabel}
              </div>
              {actionDescription ? (
                <div
                  className="create-new-section-list-description"
                  style={{ color: theme.foregroundMuted }}
                >
                  {actionDescription}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {includeMoreRow && (
        <div
          className="create-new-section-list-item"
          style={{
            backgroundColor: hoveredTypeKey === 'more' ? theme.backgroundHover : theme.backgroundMuted,
            borderColor: hoveredTypeKey === 'more' ? theme.foregroundMuted : 'transparent',
          }}
          onClick={onMoreClick}
          onMouseEnter={() => setHoveredTypeKey('more')}
          onMouseLeave={() => setHoveredTypeKey('')}
        >

          {/* LIST ICON */}
          <div className="create-new-section-list-icon">
            {renderTypeIcon({ iconKey: 'more_horiz', size: 22 })}
          </div>

          {/* LIST CONTENT */}
          <div className="create-new-section-list-content">
            <div
              className="create-new-section-list-title"
              style={{ color: theme.foreground }}
            >
              More
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ----- RENDER ----- //

  return (
    <div className="create-new-section-container">

      {/* SEARCH INPUT */}
      {showSearchInput && (
        <>
          <div
            className="create-new-section-header"
            style={{
              color: theme.foregroundMuted,
                marginBottom: 4,
            }}
          >
            ADD EXISTING
          </div>
          <div className="create-new-section-controls">
            <TextInput
              value={searchTerm}
              onChange={(event) => setSearchTerm?.(event.target.value)}
              placeholder="Search..."
              autoFocus={!isMobile}
            />
          </div>

          <Divider margin={6} />
        </>
      )}

      {/* MAIN CONTENT */}
      <div className="create-new-section-scroll-area">
        {hasSearchQuery ? (
          <FindExistingSection
            hideInput
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchError={searchError}
            searching={searching}
            noResults={noResults}
            searchResults={searchResults}
            availableTypeMap={availableTypeMap}
            anchorNodeKey={anchorNodeKey}
            attachingNodeKey={attachingNodeKey}
            enableSelectionWithoutAnchor={enableSelectionWithoutAnchor}
            onAttachExisting={onAttachExisting}
          />
        ) : (
          <>
            {/* ALL TYPES */}
            <div
              className="create-new-section-header-row"
              style={{
                marginBottom: 12,
              }}
            >
              <div
                className="create-new-section-header"
                style={{
                  color: theme.foregroundMuted,
                }}
              >
                ADD NEW
              </div>

              {/* VIEW ACTIONS */}
              <div className="create-new-section-view-actions">
                <button
                  type="button"
                  className="create-new-section-view-toggle"
                  style={{
                    backgroundColor: viewMode === VIEW_MODES.GRID ? theme.backgroundHover : 'transparent',
                    borderColor: viewMode === VIEW_MODES.GRID ? theme.foregroundMuted : 'transparent',
                  }}
                  aria-label="Show types as grid"
                  aria-pressed={viewMode === VIEW_MODES.GRID}
                  title="Grid view"
                  onClick={() => setViewMode(VIEW_MODES.GRID)}
                >
                  <Icon
                    iconKey="grid_view"
                    width={16}
                    height={16}
                    color={viewMode === VIEW_MODES.GRID ? theme.foreground : theme.foregroundMuted}
                  />
                </button>
                <button
                  type="button"
                  className="create-new-section-view-toggle"
                  style={{
                    backgroundColor: viewMode === VIEW_MODES.LIST ? theme.backgroundHover : 'transparent',
                    borderColor: viewMode === VIEW_MODES.LIST ? theme.foregroundMuted : 'transparent',
                  }}
                  aria-label="Show types as list"
                  aria-pressed={viewMode === VIEW_MODES.LIST}
                  title="List view"
                  onClick={() => setViewMode(VIEW_MODES.LIST)}
                >
                  <Icon
                    iconKey="view_list"
                    width={16}
                    height={16}
                    color={viewMode === VIEW_MODES.LIST ? theme.foreground : theme.foregroundMuted}
                  />
                </button>
              </div>
            </div>
            {viewMode === VIEW_MODES.LIST
              ? renderTypeList(displayTypes, { includeMoreRow: showMoreCard })
              : renderTypeGrid(displayTypes, { includeMoreCard: showMoreCard })}
          </>
        )}
      </div>
    </div>
  );
});

// Dev Tools
CreateNewSection.displayName = 'CreateNewSection';

export default CreateNewSection;
